import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { renderAsync } from 'docx-preview';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { LearnerService } from 'src/app/core/services/learner.service';
import { SidebarService } from 'src/app/core/services/sidebar.service';

interface SpreadsheetCell {
  value: string;
  rowSpan?: number;
  colSpan?: number;
}

interface SpreadsheetSheet {
  name: string;
  rows: (SpreadsheetCell | null)[][];
  columnLabels: string[];
  columnWidths: number[];
  rowLabels: number[];
}

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.css']
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
  pdfUrl: SafeResourceUrl | null = null;
  fileName = 'document';
  sourcePageTitle = 'Documents';
  fileTypeLabel = 'Unknown file';
  previewType: 'pdf' | 'image' | 'spreadsheet' | 'docx' | 'unsupported' = 'unsupported';
  spreadsheetSheets: SpreadsheetSheet[] = [];
  selectedSheetIndex = 0;
  isLoading = true;
  errorMessage = '';
  sidebarCollapsed = false;
  @ViewChild('docxContainer', { static: true }) docxContainer!: ElementRef<HTMLDivElement>;
  private readonly previewStateKey = 'document-preview-state';
  private fileBlob: Blob | null = null;
  private objectUrl: string | null = null;
  private sidebarSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  get hasFile(): boolean {
    return this.fileBlob !== null;
  }

  constructor(
    private learnerService: LearnerService,
    private sanitizer: DomSanitizer,
    private location: Location,
    private router: Router,
    private sidebarService: SidebarService
  ) { }

  ngOnInit(): void {
    this.sidebarSubscription = this.sidebarService.collapsed$.subscribe(collapsed => {
      this.sidebarCollapsed = collapsed;
    });

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && event.url.split(/[?#]/, 1)[0] !== '/preview') {
        this.clearStoredPreviewState();
      }
    });

    const navigationState = history.state || {};
    const hasNavigationFile = typeof navigationState.file === 'string' && navigationState.file.length > 0;
    const previewState = hasNavigationFile ? navigationState : this.restorePreviewState();
    const file = previewState && previewState.file;

    if (!file) {
      this.isLoading = false;
      this.errorMessage = 'Document details are missing. Return to the document list and open it again.';
      return;
    }

    this.sourcePageTitle = previewState.sourcePageTitle || 'Documents';
    this.storePreviewState(file, previewState.fileName, this.sourcePageTitle);
    const sourceName = this.getSourceFileName(file);
    this.fileName = this.createFileName(previewState.fileName, sourceName);
    this.learnerService.getDocumentBase64(file).subscribe(
      (response: ArrayBuffer) => { this.loadDocument(response); },
      () => {
        this.errorMessage = 'The document could not be loaded. Please try again.';
        this.isLoading = false;
      }
    );
  }

  downloadFile(): void {
    if (this.fileBlob) {
      FileSaver.saveAs(this.fileBlob, this.fileName);
    }
  }

  goBack(): void {
    this.clearStoredPreviewState();
    this.location.back();
  }

  ngOnDestroy(): void {
    if (this.sidebarSubscription) {
      this.sidebarSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  private storePreviewState(file: string, fileName: string, sourcePageTitle: string): void {
    try {
      sessionStorage.setItem(this.previewStateKey, JSON.stringify({
        file,
        fileName,
        sourcePageTitle,
        expiresAt: Date.now() + 30 * 60 * 1000
      }));
    } catch (error) {
      // Current navigation state remains available if session storage is disabled.
    }
  }

  private restorePreviewState(): { file: string; fileName: string; sourcePageTitle: string } | null {
    try {
      const storedState = sessionStorage.getItem(this.previewStateKey);
      if (!storedState) return null;

      const parsedState = JSON.parse(storedState);
      if (typeof parsedState.file === 'string' && parsedState.file && parsedState.expiresAt > Date.now()) {
        return {
          file: parsedState.file,
          fileName: parsedState.fileName || '',
          sourcePageTitle: parsedState.sourcePageTitle || 'Documents'
        };
      }
      this.clearStoredPreviewState();
    } catch (error) {
      this.clearStoredPreviewState();
    }
    return null;
  }

  private clearStoredPreviewState(): void {
    try {
      sessionStorage.removeItem(this.previewStateKey);
    } catch (error) {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  private async loadDocument(response: ArrayBuffer): Promise<void> {
    try {
      const bytes = this.decodeBase64Response(response);
      const mimeType = this.getMimeType(bytes);
      this.fileTypeLabel = mimeType;
      const fileBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      this.fileBlob = new Blob([fileBuffer], { type: mimeType });
      this.previewType = this.getPreviewType(mimeType);

      if (this.previewType === 'pdf' || this.previewType === 'image') {
        this.objectUrl = URL.createObjectURL(this.fileBlob);
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
      } else if (this.previewType === 'spreadsheet') {
        this.spreadsheetSheets = this.readSpreadsheet(bytes);
        this.selectedSheetIndex = 0;
      } else if (this.previewType === 'docx') {
        await renderAsync(this.fileBlob, this.docxContainer.nativeElement);
      } else {
        this.errorMessage = 'Preview is not available for this file type. You can download the file to open it.';
      }
    } catch (error) {
      console.error('Unable to process document response.', error);
      this.errorMessage = error instanceof Error ? error.message : 'The document could not be opened.';
    } finally {
      this.isLoading = false;
    }
  }

  private decodeBase64Response(response: ArrayBuffer): Uint8Array {
    const responseBytes = new Uint8Array(response);
    let responseText: string;

    try {
      responseText = new TextDecoder('utf-8', { fatal: true }).decode(responseBytes);
    } catch (error) {
      return responseBytes;
    }

    if (!/^[\x09\x0a\x0d\x20-\x7e]*$/.test(responseText)) {
      return responseBytes;
    }

    const base64 = this.findBase64Payload(responseText);
    const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const binary = window.atob(paddedBase64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  private findBase64Payload(value: unknown, depth: number = 0): string {
    if (typeof value !== 'string') {
      if (value && typeof value === 'object') {
        const record = value as { [key: string]: unknown };
        const keys = Object.keys(record).sort((left, right) => {
          const priority = (key: string) => /base64|content|file|document|data|body|response/i.test(key) ? 0 : 1;
          return priority(left) - priority(right);
        });
        for (const key of keys) {
          try {
            return this.findBase64Payload(record[key], depth + 1);
          } catch (error) {
            continue;
          }
        }
      }
      throw new Error('No Base64 file data was found in the document response.');
    }

    const payload = value.trim().replace(/^\uFEFF/, '');
    if (!payload) {
      throw new Error('The document API returned an empty response.');
    }

    if (depth < 4 && /^["[{]/.test(payload)) {
      try {
        const parsedResponse = JSON.parse(payload);
        if (parsedResponse !== value) {
          return this.findBase64Payload(parsedResponse, depth + 1);
        }
      } catch (error) {
        // Continue and inspect the response as a raw Base64 string.
      }
    }

    const base64 = payload
      .replace(/^data:[^,]*;base64,/i, '')
      .replace(/\s/g, '')
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/[^A-Za-z0-9+/=]/g, '')
      .replace(/=/g, '');

    if (depth < 4 && base64.length > 0) {
      try {
        const decodedResponse = window.atob(base64 + '='.repeat((4 - base64.length % 4) % 4));
        if (/^[\x20-\x7e]+$/.test(decodedResponse) && decodedResponse.length > 16) {
          return this.findBase64Payload(decodedResponse, depth + 1);
        }
      } catch (error) {
        // Keep searching other response fields when this value is not Base64.
      }
    }

    if (base64.length > 0) {
      try {
        window.atob(base64 + '='.repeat((4 - base64.length % 4) % 4));
        return base64;
      } catch (error) {
        // Continue searching other response fields.
      }
    }

    throw new Error('No Base64 file data was found in the document response.');
  }

  private getSourceFileName(file: string): string {
    const path = file.split(/[?#]/, 1)[0];
    const encodedName = path.substring(path.lastIndexOf('/') + 1);
    try {
      return decodeURIComponent(encodedName);
    } catch (error) {
      return encodedName;
    }
  }

  private createFileName(name: string, sourceName: string): string {
    const displayName = name || sourceName || 'document';
    const extensionMatch = sourceName.match(/\.[a-z0-9]{1,10}$/i);
    const withExtension = /\.[a-z0-9]{1,10}$/i.test(displayName) || !extensionMatch
      ? displayName
      : `${displayName}${extensionMatch[0]}`;
    return withExtension
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
      .trim();
  }

  private getMimeType(bytes: Uint8Array): string {
    if (this.hasSignature(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d])) return 'application/pdf';
    if (this.hasSignature(bytes, [0x89, 0x50, 0x4e, 0x47])) return 'image/png';
    if (this.hasSignature(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
    if (this.hasAsciiSignature(bytes, 'GIF8')) return 'image/gif';
    if (this.hasSignature(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
      return this.getExtension(this.fileName) === '.xls' ? 'application/vnd.ms-excel' : 'application/msword';
    }
    if (this.hasSignature(bytes, [0x50, 0x4b, 0x03, 0x04]) && this.containsAscii(bytes, 'xl/workbook.xml')) {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    const extension = this.getExtension(this.fileName);
    const mimeTypes: { [extension: string]: string } = {
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.pdf': 'application/pdf',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.gif': 'image/gif'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }

  private getPreviewType(mimeType: string): 'pdf' | 'image' | 'spreadsheet' | 'docx' | 'unsupported' {
    const extension = this.getExtension(this.fileName);
    if (extension === '.pdf' || mimeType === 'application/pdf') return 'pdf';
    if (['.jpg', '.jpeg', '.png', '.gif'].includes(extension) && mimeType.startsWith('image/')) return 'image';
    if (['.xls', '.xlsx'].includes(extension) || mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return 'spreadsheet';
    }
    if (extension === '.docx') return 'docx';
    return 'unsupported';
  }

  private readSpreadsheet(bytes: Uint8Array): SpreadsheetSheet[] {
    const workbook = XLSX.read(bytes, { type: 'array', cellStyles: true });
    return workbook.SheetNames.map(name => {
      const worksheet = workbook.Sheets[name];
      if (!worksheet || !worksheet['!ref']) {
        return { name, rows: [], columnLabels: [], columnWidths: [], rowLabels: [] };
      }

      const range = XLSX.utils.decode_range(worksheet['!ref']);
      const mergedCells = new Set<string>();
      const mergeOrigins = new Map<string, { rowSpan: number; colSpan: number }>();
      (worksheet['!merges'] || []).forEach(merge => {
        mergeOrigins.set(`${merge.s.r}:${merge.s.c}`, {
          rowSpan: merge.e.r - merge.s.r + 1,
          colSpan: merge.e.c - merge.s.c + 1
        });
        for (let row = merge.s.r; row <= merge.e.r; row++) {
          for (let column = merge.s.c; column <= merge.e.c; column++) {
            if (row !== merge.s.r || column !== merge.s.c) {
              mergedCells.add(`${row}:${column}`);
            }
          }
        }
      });

      const rows: (SpreadsheetCell | null)[][] = [];
      const rowLabels: number[] = [];
      for (let row = range.s.r; row <= range.e.r; row++) {
        rowLabels.push(row + 1);
        const cells: (SpreadsheetCell | null)[] = [];
        for (let column = range.s.c; column <= range.e.c; column++) {
          if (mergedCells.has(`${row}:${column}`)) {
            cells.push(null);
            continue;
          }

          const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
          cells.push({
            value: cell ? XLSX.utils.format_cell(cell) : '',
            ...(mergeOrigins.get(`${row}:${column}`) || {})
          });
        }
        rows.push(cells);
      }

      const columnLabels: string[] = [];
      const columnWidths: number[] = [];
      for (let column = range.s.c; column <= range.e.c; column++) {
        columnLabels.push(this.getSpreadsheetColumnLabel(column));
        const columnInfo = worksheet['!cols'] && worksheet['!cols'][column];
        const width = columnInfo && (columnInfo.wpx || (columnInfo.wch && columnInfo.wch * 7));
        columnWidths.push(width ? Math.max(48, Math.min(width, 320)) : 120);
      }

      return { name, rows, columnLabels, columnWidths, rowLabels };
    });
  }

  private getSpreadsheetColumnLabel(column: number): string {
    let label = '';
    let current = column + 1;
    while (current > 0) {
      const remainder = (current - 1) % 26;
      label = String.fromCharCode(65 + remainder) + label;
      current = Math.floor((current - 1) / 26);
    }
    return label;
  }

  private containsAscii(bytes: Uint8Array, value: string): boolean {
    for (let start = 0; start <= bytes.length - value.length; start++) {
      if (this.hasAsciiSignature(bytes, value, start)) return true;
    }
    return false;
  }

  private getExtension(fileName: string): string {
    const match = fileName.toLowerCase().match(/\.[a-z0-9]{1,10}$/);
    return match ? match[0] : '';
  }

  private hasSignature(bytes: Uint8Array, signature: number[]): boolean {
    return signature.every((value, index) => bytes[index] === value);
  }

  private hasAsciiSignature(bytes: Uint8Array, signature: string, offset: number = 0): boolean {
    return signature.split('').every((character, index) => bytes[offset + index] === character.charCodeAt(0));
  }
}